import * as React from 'react';

import { CloseIcon, TooltipModel } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { PortalTooltip } from '@components/PortalTooltip';
import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import { VariantDisplayTypes } from '@containers/ProductListing/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { hexToDec, dataLabelsProp } from '@helpers/utils';

import { trackVariantClick } from '../../tracking';
import { VariantLink, VariantListItem } from '../../variantsStyled';

import { StyledColorRectangle, Variant } from './styled';

export interface IVariantItemProps {
  item: CatalogVariantFragmentFragment;
  isSelected: boolean;
}

export const getContrastingIconColor = (colors: string[]) => {
  if (colors.length === 0) {
    return 'icon.primary';
  }

  const avgColor =
    colors
      .map(
        (c) =>
          0.2126 * hexToDec(c, 0) +
          0.7152 * hexToDec(c, 2) +
          0.0722 * hexToDec(c, 4)
      )
      .reduce((a, b) => a + b) / colors.length;

  return avgColor > 0.179 ? 'icon.inverse' : 'icon.primary';
};

export const ColorPicker: React.FC<IVariantItemProps> = ({
  item,
  isSelected,
}) => {
  const newDesign = useNewPdDesignEnabled();
  const { getTimeFromInit } = useTrackingContext();
  const isOnStock = item.stockAvailability.code !== IStock.outOfStock;

  const withoutColor = !item.colors || item.colors.length === 0;
  const colors = withoutColor ? ['icon.primary'] : item.colors;

  const handleClick = () =>
    trackVariantClick(getTimeFromInit(), VariantDisplayTypes.ColorPicker);

  const ColorTile = (
    <StyledColorRectangle
      width={2}
      height={2}
      colors={colors}
      newDesign={newDesign}
      bordered={withoutColor && !isSelected}
    >
      {!isOnStock && (
        <CloseIcon
          color={getContrastingIconColor(colors)}
          width={newDesign ? '1.25rem' : '30px'}
          height={newDesign ? '1.25rem' : '30px'}
        />
      )}
    </StyledColorRectangle>
  );

  return (
    <VariantListItem
      minWidth="auto"
      data-testid={
        isSelected ? 'color-picker-item-selected' : 'color-picker-item'
      }
    >
      <ErrorBoundary>
        <VariantLink
          onClick={handleClick}
          replace={true}
          to={item.url}
          id={`pd-variant-${item.webId}`}
          className={isSelected ? 'pd-variant-selected' : ''}
          {...dataLabelsProp(item.attributes)}
        >
          <Variant
            newDesign={newDesign}
            touch={false}
            isSelected={isSelected}
            isOnStock={isOnStock}
          >
            <PortalTooltip
              disabled={!item.additionalInfo}
              position={TooltipModel.Position.topRight}
              content={
                <PortalTooltip.Content shouldWrapText={false}>
                  {item.additionalInfo}
                </PortalTooltip.Content>
              }
            >
              {ColorTile}
            </PortalTooltip>
          </Variant>
          <Variant
            newDesign={newDesign}
            touch={true}
            isSelected={isSelected}
            isOnStock={isOnStock}
          >
            {ColorTile}
          </Variant>
        </VariantLink>
      </ErrorBoundary>
    </VariantListItem>
  );
};
